import { Box, Button, Popover } from "@mui/material";
import CalendarToday from "@mui/icons-material/CalendarToday";
import Cancel from "@mui/icons-material/Cancel";
import RepeatIcon from "@mui/icons-material/Repeat";
import moment from "moment";
import { useRef, useState } from "react";
import { Repeat } from "../../../api/Tasks";
import { DueDatePanel } from "./DueDatePanel";

export function DueDateButton(props: {
    dueDate: string;
    dueTime: string;
    repeat: Repeat;
    onChange: (changes: { dueDate?: string; dueTime?: string; repeat?: Repeat }) => void;

    // hideRepeat drops the repeat controls, for a task that can't carry one (see
    // core/task)
    hideRepeat?: boolean;
}) {
    const anchor = useRef<HTMLButtonElement>(null);
    const [open, setOpen] = useState(false);

    let label = "Add due date";
    if (props.dueDate) {
        const due = moment(props.dueDate);
        label = due.format(due.isSame(moment(), "year") ? "MMM D" : "MMM D, YYYY");
        if (props.dueTime) label += " " + moment(props.dueTime, "HH:mm").format("h:mma");
    }

    return (
        <>
            <Button
                ref={anchor}
                size="small"
                color={props.dueDate ? "primary" : "inherit"}
                startIcon={<CalendarToday fontSize="inherit" />}
                endIcon={props.dueDate || (props.repeat.kind && !props.hideRepeat) ? (
                    <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                        {props.repeat.kind && !props.hideRepeat ? <RepeatIcon fontSize="inherit" /> : null}
                        {props.dueDate ? (
                            // a span rather than a button, since this one sits inside one.
                            // The click is stopped so clearing doesn't also open the panel.
                            <Box
                                component="span"
                                role="button"
                                aria-label="Clear due date"
                                onClick={e => {
                                    e.stopPropagation();
                                    props.onChange({ dueDate: "", dueTime: "" });
                                }}
                                sx={{
                                    display: "flex",
                                    opacity: 0.6,
                                    "&:hover": { opacity: 1 },
                                }}
                            >
                                <Cancel fontSize="inherit" />
                            </Box>
                        ) : null}
                    </Box>
                ) : undefined}
                sx={{
                    borderRadius: 5, borderStyle: props.dueDate ? "solid" : "dashed",
                    textTransform: "none", fontSize: 12, minWidth: 0, py: 0.125, px: 1,
                    "& .MuiButton-startIcon": { mr: 0.5 },
                    "& .MuiButton-endIcon": { ml: 0.5 },
                }}
                onClick={() => setOpen(true)}
            >
                {label}
            </Button>

            <Popover
                open={open}
                anchorEl={anchor.current}
                onClose={() => setOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
                <DueDatePanel
                    hideRepeat={props.hideRepeat}
                    dueDate={props.dueDate}
                    dueTime={props.dueTime}
                    repeat={props.repeat}
                    onChange={props.onChange}
                />
            </Popover>
        </>
    );
}
